/**
 * @fileoverview Hook Actions Layer para operaciones de negocio de transacción de pago.
 * @module usePaymentTransactionActions
 * @category Hook - Actions Layer
 * @since 3.1.0
 * @version 3.1.0
 */
import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import paymentTransactionModelMC from '../../../models/PaymentTransactionModelMC';

/**
 * Hook Actions Layer para guardar la transacción de pago.
 *
 * - Solo maneja operaciones de negocio y side effects.
 * - No contiene lógica de formulario ni UI.
 * - Optimizaciones: useCallback para handler.
 *
 * @function usePaymentTransactionActions
 * @param {Object} params
 * @param {Object} params.order - Orden relacionada
 * @param {string} params.newStatus - Nuevo estado de pago
 * @param {function} params.updateOrderInList - Callback para actualizar orden
 * @param {function} params.toggleModal - Callback para cerrar modal
 * @param {Object} params.formMethods - Métodos de react-hook-form
 * @returns {Object} { loading, handleSaveButtonClick }
 * @see usePaymentTransactionForm
 * @see usePaymentTransactionUI
 */
export function usePaymentTransactionActions({ order, newStatus, updateOrderInList, toggleModal, formMethods }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  /**
   * Transforma los datos del formulario a FormData para la API.
   * @param {Object} data - Datos del formulario
   * @returns {FormData}
   */
  const transformFormData = useCallback((data) => {
    const formData = new FormData();
    const file = data.attachment?.length > 0 && data.attachment[0] ? data.attachment[0] : 'null';
    formData.append('payment_transaction[note]', data.note);
    formData.append('payment_transaction[status]', newStatus.replace('payment_', ''));
    formData.append('payment_transaction[service]', 'manual');
    formData.append('payment_transaction[attachment]', file);
    formData.append('payment_transaction[payment_method]', data.paymentMethod);
    formData.append('payment_transaction[custom_payment_method]', data.paymentOther);
    return formData;
  }, [newStatus]);

  /**
   * Handler para guardar la transacción de pago.
   * @function handleSaveButtonClick
   * @memberof usePaymentTransactionActions
   * @returns {void}
   */
  const handleSaveButtonClick = useCallback(() => {
    formMethods.handleSubmit(async (data) => {
      setLoading(true);
      const params = transformFormData(data);
      try {
        const paymentTransaction = await paymentTransactionModelMC.saveItem(order.id, params);
        updateOrderInList({ ...order, payment_status: newStatus, payment_transaction: paymentTransaction });
        toggleModal();
      } catch (error) {
        dispatch({
          type: LOAD_DATA_FAILURE,
          error: error?.message || 'Error saving payment transaction'
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [formMethods, dispatch, order, toggleModal, transformFormData, updateOrderInList, newStatus]);

  return {
    loading,
    handleSaveButtonClick
  };
}
